import React, { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import Modal from 'LuxKit/Modal/Modal'
import BodyText from 'LuxKit/Typography/BodyText'
import LineCommentsIcon from 'LuxKit/Icons/line/LineCommentsIcon'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import { connect } from 'react-redux'
import { getSVGImageUrl } from 'lib/image/imageUtils'
import config from 'constants/config'
import shouldRenderTimely, { getTimelyLink } from 'lib/timely/timely'
import { isSupportAssistantEnabled } from 'selectors/featuresSelectors'
import { useRenderWhatsApp } from 'hooks/useRenderWhatsApp'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import ContactContext from 'contexts/contactContext'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { NO_24_7_SUPPORT_REGIONS } from 'constants/config/region'

interface Props {
  currentRegionCode: string
  supportAssistantEnabled: boolean
  supportAssistantFirst?: boolean
}

function HeaderContactOptionsModal(props: Props) {
  const {
    currentRegionCode,
    supportAssistantEnabled,
    supportAssistantFirst = false,
  } = props
  const intl = useIntl()
  const modalElement = useModalElementContext()
  const currentContact = useContext(ContactContext)
  const shouldRenderWhatsApp = useRenderWhatsApp(currentRegionCode)

  const localContact = currentContact.local
  const internationalContact = currentContact.international

  const copy = useMemo(() => {
    const isIndia = currentRegionCode.toUpperCase() === 'IN'

    if (isIndia) {
      return {
        title: intl.formatMessage(
          {
            id: 'd71sYQCKAbswpGARtp75',
            defaultMessage: 'We’re your travel companion',
            description: 'Contact options modal title for India region in the support area',
          },
        ),
        message: intl.formatMessage(
          {
            id: 'BuxOdtZL5HFN_nmGuKJI',
            defaultMessage: 'Our customer service team is available online or on the phone 7 days a week, 9am to 7pm.',
            description: 'Contact options modal message about availability for India region',
          },
        ),
      }
    }

    return {
      title: intl.formatMessage(
        {
          id: 'DBunl_1f_ym4H4LJwkr3',
          defaultMessage: 'We’re your 24/7 travel companion',
          description: 'Contact options modal title for default regions in the site header',
        },
      ),
      message: intl.formatMessage(
        {
          id: 'yy3UNJX9VlwiXkOVxFj0',
          defaultMessage: 'Our customer service team is available online or on the phone all the time, whenever you need us.',
          description: 'Contact options modal message about availability for default regions',
        },
      ),
    }
  }, [intl, currentRegionCode])

  const handleItemClick = useCallback(() => {
    modalElement.resolve()
  }, [modalElement])

  return (
    <Modal title={copy.title}>
      <VerticalSpacer gap={16}>
        <BodyText variant="large">{copy.message}</BodyText>
        <div>
          {supportAssistantFirst && supportAssistantEnabled && <ClickableListItem
            title={intl.formatMessage(
              {
                id: '8bzw1OOoEurvbUU7gj6S',
                defaultMessage: 'Chat now',
                description: 'Title attribute for the live chat support button in the contact options',
              },
            )}
            startIcon={<LineCommentsIcon />}
            subtitle={intl.formatMessage(
              {
                id: 'soT7i2OCvt9G5ycA1x64',
                defaultMessage: 'Luxury Escapes Support Assistant',
                description: 'Subtitle text for the support assistant chat widget identifying the brand name',
              },
            )}
            onClick={handleItemClick}
            to="/support"
            aria-label={intl.formatMessage(
              {
                id: 'zpppgcXgJHsFAUYPjbJo',
                defaultMessage: 'Luxury Escapes Support Assistant',
                description: 'Accessible aria-label for the support assistant chat widget launcher button',
              },
            )}
            rel="nofollow"
            target="_blank"
          />}
          {shouldRenderTimely(currentRegionCode) &&
            <ClickableListItem
              title={intl.formatMessage(
                {
                  id: 'PKqJEqW8RY8Ha_AdjR0x',
                  defaultMessage: 'Book store appointment',
                  description: 'Book store appointment button title in the site header',
                },
              )}
              startIcon={<img
                src={getSVGImageUrl('logo-LE')}
                width="24"
                height="24"
                alt={intl.formatMessage({
                  id: 'NdLD_3mTeCcHchmgr-5a',
                  defaultMessage: 'LE store',
                  description: 'Alt text for Luxury Escapes store icon in contact options modal',
                })}
              />}
              onClick={handleItemClick}
              href={getTimelyLink()}
              aria-label={intl.formatMessage(
                {
                  id: '2JF3qKwMgInCpsFS5sBG',
                  defaultMessage: 'Timely booking',
                  description: 'Accessible aria-label for the timely booking urgency indicator on the offer card',
                },
              )}
              rel="noopener"
              target="_blank"
            />}
          {!NO_24_7_SUPPORT_REGIONS.includes(currentRegionCode) && (
            <ClickableListItem
              title={intl.formatMessage(
                {
                  id: '76RtWufxcrd0A0lFGMYr',
                  defaultMessage: 'Local Sales & Support',
                  description: 'Local Sales & Support button title in the customer support section',
                },
              )}
              startIcon={<LinePhoneIcon />}
              subtitle={localContact.humanReadable}
              onClick={handleItemClick}
              href={`tel:${localContact.number}`}
              aria-label={intl.formatMessage(
                {
                  id: 'kM8OWGQcL8dUpzMFie46',
                  defaultMessage: 'Sales & Support contact',
                  description: 'Local Sales & Support aria label in the customer support section',
                },
              )}
              rel="nofollow"
            />
          )}
          {!supportAssistantFirst && supportAssistantEnabled && <ClickableListItem
            title={intl.formatMessage(
              {
                id: '8bzw1OOoEurvbUU7gj6S',
                defaultMessage: 'Chat now',
                description: 'Title attribute for the live chat support button in the contact options',
              },
            )}
            startIcon={<LineCommentsIcon />}
            subtitle={intl.formatMessage(
              {
                id: 'soT7i2OCvt9G5ycA1x64',
                defaultMessage: 'Luxury Escapes Support Assistant',
                description: 'Subtitle text for the support assistant chat widget identifying the brand name',
              },
            )}
            onClick={handleItemClick}
            to="/support"
            aria-label={intl.formatMessage(
              {
                id: 'zpppgcXgJHsFAUYPjbJo',
                defaultMessage: 'Luxury Escapes Support Assistant',
                description: 'Accessible aria-label for the support assistant chat widget launcher button',
              },
            )}
            rel="nofollow"
            target="_blank"
          />}
          {shouldRenderWhatsApp &&
            <ClickableListItem
              title={intl.formatMessage(
                {
                  id: 'l9RLaA9i_zj4lbeaSd_u',
                  defaultMessage: 'WhatsApp',
                  description: 'Title attribute for the WhatsApp contact button in the customer support section',
                },
              )}
              startIcon={
                <img
                  src={getSVGImageUrl('whatsapp-logo')}
                  width="24"
                  height="24"
                  alt={intl.formatMessage({
                    id: 'xTjcr6T-VXNwPySsGRmn',
                    defaultMessage: 'WhatsApp',
                    description: 'Alt text for WhatsApp logo in header contact options modal',
                  })}
                />
              }
              onClick={handleItemClick}
              href={`https://wa.me/${config.WHATS_APP_NUMBER}`}
              aria-label={intl.formatMessage(
                {
                  id: 'BBO7ZdxFiRiiDMZ2HgT4',
                  defaultMessage: 'WhatsApp',
                  description: 'Accessible aria-label for the WhatsApp contact link in the header options',
                },
              )}
              rel="noopener"
              target="_blank"
            />}
          <ClickableListItem
            startIcon={<LinePhoneIcon />}
            title={intl.formatMessage(
              {
                id: 'p6zMhwChp1gfO6rFVlci',
                defaultMessage: 'International Support Centre',
                description: 'International Support Centre button title in the support area',
              },
            )}
            subtitle={internationalContact.humanReadable}
            onClick={handleItemClick}
            href={`tel:${internationalContact.number}`}
            aria-label={intl.formatMessage(
              {
                id: 'N7Y7el3C2UBMOsiNdU_G',
                defaultMessage: 'International Support Centre contact',
                description: 'International Support Centre aria label in the customer support section',
              },
            )}
            rel="nofollow"
          />
        </div>
      </VerticalSpacer>
    </Modal>
  )
}

const mapStateToProps = (state: App.State) => ({
  currentRegionCode: state.geo.currentRegionCode,
  supportAssistantEnabled: isSupportAssistantEnabled(state),
})

export default connect(mapStateToProps)(HeaderContactOptionsModal)
